import type { IconProps } from './create-icon';
import { icons } from './registry';
import { boldIconVariants, bulkIconVariants, filledIconVariants } from './variants';

export type UnitfieldIconName = keyof typeof icons;
export type UnitfieldIconVariant = 'light' | 'bold' | 'bulk' | 'filled';
export type UnitfieldIconProps = IconProps & {
  /** Registered names autocomplete; external metadata is validated at render time. */
  name: UnitfieldIconName | (string & Record<never, never>);
  /** Actual provider geometry. Filled prefers Bold, then Bulk. */
  variant?: UnitfieldIconVariant;
};

export const unitfieldIconNames = Object.freeze(
  Object.keys(icons) as UnitfieldIconName[],
);

export function isUnitfieldIconName(value: unknown): value is UnitfieldIconName {
  return typeof value === 'string' && Object.hasOwn(icons, value);
}

/** Every product icon is inline during SSR; unknown metadata has a local fallback. */
export function UnitfieldIcon({ name, variant, ...props }: UnitfieldIconProps) {
  const registered = isUnitfieldIconName(name);
  const variants: Readonly<Partial<Record<UnitfieldIconName, UnitfieldIconName>>> =
    variant === 'filled' ? filledIconVariants : variant === 'bold' ? boldIconVariants : variant === 'bulk' ? bulkIconVariants : {};
  const target = registered && Object.hasOwn(variants, name)
    ? variants[name]
    : undefined;
  const resolvedVariant = target
    ? (variant === 'filled' ? (Object.hasOwn(boldIconVariants, name) ? 'bold' : 'bulk') : variant)
    : variant ? 'light' : undefined;
  const Component = icons[target ?? (registered ? name : 'help-circle')];
  return <Component data-unitfield-icon-variant={resolvedVariant} data-unrecognized-icon={registered ? undefined : name} {...props} />;
}
